/**
 * Diff Inline Overlay
 *
 * All logic runs via event delegation from document level.
 *
 * - Sizes `.diff-inline-wrap` elements to the scroll container width
 * - Handles `.diff-comment-btn` and `.diff-comment-btn-reply` clicks
 *   by injecting a comment form into the DOM (no server round-trip)
 * - Form submit pushes `save_inline_comment` to the LiveView
 */

import escapeHtml from "escape-html";
import { illuminateCssClassDiff } from "../../.dox/ui";
import { animateInlineCommentFormIn } from "./hooks/inline-comment-form";
import type { PrPushEventWire } from "./hooks/types";

// CSS class names shared with HEEx (`lib/operation_web/live/diff_component.ex`)
// and Wallaby tests; pinned through the `CssClassDiff` paradox union so a
// typo on either side is a compile-time error, not a silent no-op.
const DIFF_COMMENT_BTN = illuminateCssClassDiff("diffCommentBtn");
const DIFF_COMMENT_BTN_REPLY = illuminateCssClassDiff("diffCommentBtnReply");
const DIFF_INLINE_WRAP = illuminateCssClassDiff("diffInlineWrap");
const DIFF_INLINE_CELL = illuminateCssClassDiff("diffInlineCell");
const DIFF_INLINE_FORM_CLIENT = illuminateCssClassDiff("diffInlineFormClient");
const INLINE_COMMENT_FORM = illuminateCssClassDiff("inlineCommentForm");
const DIFF_INLINE_CANCEL = "diff-inline-cancel"; // local-only, no HEEx counterpart

interface DiffHook {
  el: HTMLElement;
  pushEvent(event: PrPushEventWire, payload: Record<string, string>): void;
}

// Per-element state attached during `attachDiffInteractions`. Holds the
// LiveView hook reference (so the form-submit handler can push events
// back to the server) and the teardown thunk for the resize listener.
// Keying both on `hook.el` via one WeakMap lets the GC reclaim the
// whole record the moment the element is detached, and lets
// `detachDiffInteractions` clear the hook reference in lockstep with
// the cleanup — the previous two-map split silently leaked the hook
// reference past detach.
type CleanupFn = () => void;
interface DiffElementState {
  readonly hook: DiffHook;
  readonly cleanup: CleanupFn;
}
const elementState = new WeakMap<Element, DiffElementState>();

interface PhxHook {
  mounted(): void;
  destroyed(): void;
}

// Size all .diff-inline-wrap elements inside a container to its visible width
function sizeWraps(container: HTMLElement): void {
  const width: number = container.clientWidth;
  container
    .querySelectorAll<HTMLElement>(`.${DIFF_INLINE_WRAP}`)
    .forEach((el: HTMLElement): void => {
      el.style.width = `${width}px`;
      el.style.position = "sticky";
      el.style.left = "0";
    });
}

// Monotonic counter for unique form IDs — no collision possible
let formIdCounter = 0;

// Pure: generate a unique form ID (monotonic, no path-based collision)
export function commentFormId(): string {
  return `inline-form-client-${++formIdCounter}`;
}

// Create and inject a comment form after the given <tr>
function openCommentForm(
  tr: HTMLElement,
  file: string,
  line: string,
  hookEl: HTMLElement,
): void {
  // Toggle if already open — match on exact file+line via data attributes.
  // Iterate rather than interpolate into querySelector to avoid CSS selector injection
  // from file paths containing "]
  const existing = Array.from(
    document.querySelectorAll<HTMLElement>(`.${DIFF_INLINE_FORM_CLIENT}`),
  ).find(
    (el) => el.dataset["diffFile"] === file && el.dataset["diffLine"] === line,
  );
  if (existing) {
    existing.remove();
    return;
  }

  // Remove any other open client-side comment forms
  document
    .querySelectorAll(`.${DIFF_INLINE_FORM_CLIENT}`)
    .forEach((el: Element): void => {
      el.remove();
    });

  const formRow: HTMLTableRowElement = document.createElement("tr");
  formRow.className = DIFF_INLINE_FORM_CLIENT;
  formRow.id = commentFormId();
  formRow.dataset["diffFile"] = file;
  formRow.dataset["diffLine"] = line;
  formRow.innerHTML = `
    <td colspan="4" class="${DIFF_INLINE_CELL}">
      <div class="${DIFF_INLINE_WRAP} bg-surface/30 p-2">
        <form class="${INLINE_COMMENT_FORM} space-y-2">
          <input type="hidden" name="file" value="${escapeHtml(file)}" />
          <input type="hidden" name="line" value="${escapeHtml(line)}" />
          <textarea name="body" rows="3" placeholder="Write a comment..."
            class="w-full rounded-lg bg-base-dark border border-border px-3 py-2 text-text text-xs font-sans"></textarea>
          <div class="flex justify-end gap-2">
            <button type="button" class="${DIFF_INLINE_CANCEL} rounded-lg px-2.5 py-1 text-xs font-medium text-text-muted hover:text-text">
              Cancel
            </button>
            <button type="submit"
              class="rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-base hover:bg-accent-hover">
              Comment
            </button>
          </div>
        </form>
      </div>
    </td>
  `;

  tr.after(formRow);

  // Match the heex `<.inline_comment_form>` entrance — fade + slide + rotateX.
  const formEl = formRow.querySelector<HTMLElement>(
    `form.${INLINE_COMMENT_FORM}`,
  );
  if (formEl) animateInlineCommentFormIn(formEl);

  // Size the wrap to container width
  const container: HTMLElement | null = tr.closest<HTMLElement>(
    "[phx-hook='diffHighlight']",
  );
  if (container) {
    const wrap: HTMLElement = formRow.querySelector<HTMLElement>(
      `.${DIFF_INLINE_WRAP}`,
    )!;
    wrap.style.width = `${container.clientWidth}px`;
    wrap.style.position = "sticky";
    wrap.style.left = "0";
  }

  const textarea: HTMLTextAreaElement = formRow.querySelector("textarea")!;
  textarea.focus();

  formRow
    .querySelector(`.${DIFF_INLINE_CANCEL}`)!
    .addEventListener("click", (): void => {
      formRow.remove();
    });

  formRow
    .querySelector("form")!
    .addEventListener("submit", (e: Event): void => {
      e.preventDefault();
      const body: string = textarea.value.trim();
      if (!body) return;

      // Push event to LiveView via the hook reference stored by attachDiffInteractions
      const state = elementState.get(hookEl);
      if (state) {
        state.hook.pushEvent("saveInlineComment", {
          file,
          line,
          body,
        });
      } else {
        console.warn(
          "diff-inline-overlay: no LiveView hook found on container, comment not saved",
        );
      }
      formRow.remove();
    });
}

// Global click delegation — registered once
let _globalClickAttached: boolean = false;

function ensureGlobalClickHandler(): void {
  if (_globalClickAttached) return;
  _globalClickAttached = true;

  document.addEventListener("click", (e: Event): void => {
    const target: HTMLElement = e.target as HTMLElement;
    const btn: HTMLElement | null = target.closest<HTMLElement>(
      `.${DIFF_COMMENT_BTN}, .${DIFF_COMMENT_BTN_REPLY}`,
    );
    if (!btn) return;

    const file: string | undefined = btn.dataset["file"];
    const line: string | undefined = btn.dataset["line"];
    if (!file || !line) return;

    const tr: HTMLElement = btn.closest("tr")!;
    const container: HTMLElement | null = btn.closest<HTMLElement>(
      "[phx-hook='diffHighlight']",
    );
    if (!container) return;

    openCommentForm(tr, file, line, container);
  });
}

// Register global click handler at module load — works even if hooks don't mount
ensureGlobalClickHandler();

// Attach to a DiffHighlight container — called from the hook
export function attachDiffInteractions(
  container: HTMLElement,
  hook: DiffHook,
): void {
  // Size existing inline wraps
  sizeWraps(container);

  // Re-size on window resize
  const resizeHandler: () => void = (): void => {
    sizeWraps(container);
  };
  window.addEventListener("resize", resizeHandler, { passive: true });

  // Call sites pass `attachDiffInteractions(this.el, this)`, so
  // container === hook.el; key on hook.el so detach can match without
  // a second lookup. The single record carries both the hook (for
  // pushEvent from the submit handler) and the cleanup thunk.
  elementState.set(hook.el, {
    hook,
    cleanup: () => {
      window.removeEventListener("resize", resizeHandler);
    },
  });
}

export function detachDiffInteractions(hook: DiffHook): void {
  const state = elementState.get(hook.el);
  if (state) {
    state.cleanup();
    elementState.delete(hook.el);
  }
}

// Kept for backwards compat in hook registration
export const DiffInlineWrap: PhxHook = {
  mounted(): void {},
  destroyed(): void {},
};
export const DiffCommentBtn: PhxHook = {
  mounted(): void {},
  destroyed(): void {},
};

// Test-only accessor: surfaces the per-element WeakMap slot so the
// lifecycle test can prove attach populates it and detach clears it.
// The slot's presence is the load-bearing fix for the LE2 hook-leak
// bug — when state lived in two parallel maps, detach forgot to clear
// the hook ref. Keep the slot under test so a regression to a two-map
// split (or to forgetting `elementState.delete`) is caught.
export const __testing = {
  getElementState: (el: Element): DiffElementState | undefined =>
    elementState.get(el),
};
